import { ModelConnectionTest } from '@/components/admin/model-connection-test';
import { ModelPanel } from '@/components/admin/model-panel';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { modelSettingsApi, testModelParams } from '@/lib/api';
import { modelRefusalSentence } from '@/lib/model-labels';
import type { ModelSettingsDraft } from '@/lib/model-settings-draft';
import { MODEL_SETTINGS_DRAFT_KIT, draftOf } from '@/lib/model-settings-draft';
import { MODEL_SETTINGS_WORDING } from '@/lib/model-settings-wording';
import { useIsVitrine } from '@/lib/use-instance';
import type {
  ModelCatalogView,
  ModelNumericPath,
  ModelParams,
  ModelParamsView,
  ModelSeat,
  ModelSeatKey,
  ModelSettingsContent,
  ModelSettingsDetail,
  ModelSettingsDraftContent,
  ModelSettingsVersion,
  ModelTextPath,
  VersionSummary,
} from '@matter_extractor/shared';
import { writeModelNumber, writeModelSetting } from '@matter_extractor/shared';
import { useMutation } from '@tanstack/react-query';

/**
 * The editor of « Les modèles » (vitrine.md section 5, issue #192): the pattern
 * of §4 in its `settings` logement, plus the form of the five seats.
 *
 * Everything the other editors do identically - the head, the history, the save
 * box and « Passer en production », the debounced autosave and its optimistic
 * control, undo and redo - is `VersionedEditor`. What is here is what only the
 * models know: their API, their draft kit, their wording and the panel of the
 * seats, which became the DRAFT: a tile clicked, a tariff typed are an autosave,
 * and « Enregistrer une nouvelle version » is the one write.
 *
 * ## The panel keeps what it always read beside the form
 *
 * `view` is `GET /api/models/params`: the first-boot seed (the left column of a
 * seat), the published tariffs that prefill a seat that moves, the warnings and
 * the journal of the moves made before the versions. The seats the panel
 * measures a move against are the `développement` version's, from the detail.
 *
 * ## The cache keys
 *
 * `['model-settings', email, MODEL_SETTINGS_ID]` holds the detail and the layer
 * writes every answer into it. There is no list to invalidate - a singleton has
 * none - so the second prefix names one nobody reads, rather than the detail's
 * own, which a prefix invalidation would refetch after every autosave.
 */

/** The prefix of the detail's query key, shared with the route that reads it. */
export const MODEL_SETTINGS_QUERY_KEY = 'model-settings';

export interface ModelSettingsEditorProps {
  readonly detail: ModelSettingsDetail;
  /** A version opened read-only by « Voir », or null. */
  readonly viewing: ModelSettingsVersion | null;
  /** The reading beside the form: the seed, the tariffs, the warnings, the old journal. */
  readonly view: ModelParamsView;
  readonly catalogView?: ModelCatalogView | undefined;
  readonly onCloseView: () => void;
  readonly onView: (version: VersionSummary) => void;
  /** Re-read the models from the server - what the stale-draft box offers. */
  readonly onReload: () => void;
}

export function ModelSettingsEditor({
  detail,
  viewing,
  view,
  catalogView,
  onCloseView,
  onView,
  onReload,
}: ModelSettingsEditorProps) {
  return (
    <VersionedEditor<ModelSettingsContent, ModelSettingsDraftContent, ModelSettingsDraft>
      wording={MODEL_SETTINGS_WORDING}
      api={modelSettingsApi}
      kit={MODEL_SETTINGS_DRAFT_KIT}
      keys={{ detail: MODEL_SETTINGS_QUERY_KEY, list: 'model-settings-sans-liste' }}
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      onCreated={() => undefined}
      layout="settings"
      // A typed 400 of the save (a negative tariff, a seat that cannot take its
      // sampling) is worded from its code; the layer falls back on its own.
      ownRefusal={modelRefusalSentence}
      renderFields={({ draft, readOnly, onDraft }) => (
        <ModelFields
          draft={draft}
          readOnly={readOnly}
          view={view}
          stored={detail.developmentContent.params}
          catalogView={catalogView}
          onDraft={onDraft}
        />
      )}
    />
  );
}

interface ModelFieldsProps {
  readonly draft: ModelSettingsDraft;
  readonly readOnly: boolean;
  readonly view: ModelParamsView;
  readonly stored: ModelParams;
  readonly catalogView: ModelCatalogView | undefined;
  readonly onDraft: (next: ModelSettingsDraft) => void;
}

/**
 * The form of the five seats and the budget, as the layer's `renderFields`.
 *
 * Three small writers, each building the next draft from the one it holds: the
 * panel speaks `ModelParams` and a draft is the same params under the fixed
 * name, so the cross is `draftOf`.
 */
function ModelFields({ draft, readOnly, view, stored, catalogView, onDraft }: ModelFieldsProps) {
  const candidate: ModelParams = draft.params;

  /**
   * One TEXT setting: a model id or an endpoint typed by hand.
   *
   * Never a route. A door is chosen by clicking a tile, which moves the whole
   * seat through `seat()` below - so the pair « endpoint beside the direct
   * route », which the seat schema refuses outright, is unrepresentable from
   * this screen rather than repaired after the fact.
   */
  function change(path: ModelTextPath, value: string | null) {
    onDraft(draftOf(writeModelSetting(candidate, path, value)));
  }

  /** One euro figure - a seat's tariff, or the budget of §11.2. */
  function number(path: ModelNumericPath, value: number) {
    onDraft(draftOf(writeModelNumber(candidate, path, value)));
  }

  /**
   * A whole seat at once - route, model, endpoint AND tariff - because a tile
   * names ONE serve at ONE published rate: writing them one at a time would put
   * the draft through states nobody chose.
   */
  function seat(key: ModelSeatKey, next: ModelSeat) {
    onDraft(draftOf({ ...candidate, [key]: next }));
  }

  /**
   * The connection test (BUILD-835). Its own mutation, never folded into the
   * save: it writes nothing, and its result survives every keystroke until the
   * next run - a verdict that vanished on the first click would be unreadable
   * exactly when it matters. It tests the values of the FORM - the draft, or the
   * version « Voir » shows - and never what the base holds.
   */
  const test = useMutation({ mutationFn: () => testModelParams(candidate) });
  // A paid call, so the vitrine has none (vitrine.md section 6, H8): it is made on the atelier.
  const vitrine = useIsVitrine();

  return (
    <div className="space-y-8">
      <ModelPanel
        view={view}
        stored={stored}
        readOnly={readOnly}
        catalogView={catalogView}
        candidate={candidate}
        onChange={change}
        onNumber={number}
        onSeat={seat}
      />

      {vitrine ? null : (
        <ModelConnectionTest
          results={test.data?.results ?? null}
          running={test.isPending}
          failed={test.isError}
          onRun={() => test.mutate()}
        />
      )}
    </div>
  );
}
